import { featureFlags } from '../../config';
import { questionTypes } from '../../constants';

export const questionValidator = (isMultiSelect, answerValue) => {
  if (isMultiSelect) return true;
  if (answerValue) return true;
  return false;
};

/**
 * Determine if proof should be asked for based on the selected answers.
 * The function returns an object indicating what proof is required and what is missing.
 * @param {Answer} answer - The answer object
 * @param {Question} question - The question object
 * @param {Number} nrOfUploadedImages - The number of uploaded images
 * @returns {{
 *  isCommentMissing: boolean,
 *  isUploadMissing: boolean,
 *  isProofMissing: boolean
 * }}
 */
export const shouldAskForProof = (answer, question, nrOfUploadedImages) => {
  // debugger;
  const requiresComment = question.commentsTextBox === 'Open Text shown';
  const requiresUpload = question.upload;

  const result = {
    isCommentMissing: false,
    isUploadMissing: false,
    isProofMissing: false,
  };

  if (
    !answer ||
    !answer.selectedAnswer ||
    answer.selectedAnswer.length === 0 ||
    (!requiresComment && !requiresUpload)
  ) {
    return result;
  }

  const { selectedAnswer: selectedAnswersIds, comment } = answer;
  const { answersExemptFromAttachments } = question;

  const answerIdsExemptFromAttachments =
    answersExemptFromAttachments?.map((ans) => ans.answerId) ?? [];

  // NON_BINARY_SINGLE_CHOICE stores sub-answer IDs at index [1+] in selectedAnswer.
  // Sub-answer IDs are not main answer options and must not influence the exempt check.
  // All other question types only have main answer IDs in selectedAnswer so every() is correct.
  const idsToCheckForExemption =
    question.questionType === questionTypes.NON_BINARY_SINGLE_CHOICE
      ? selectedAnswersIds.slice(0, 1)
      : selectedAnswersIds;

  const allSelectedAreExempt =
    idsToCheckForExemption.length > 0 &&
    idsToCheckForExemption.every((id) =>
      answerIdsExemptFromAttachments.includes(id)
    );

  if (allSelectedAreExempt) {
    return result;
  }

  const didComment = comment && comment.length > 0;
  const didUploadImages = nrOfUploadedImages > 0;

  result.isCommentMissing = requiresComment && !didComment;
  result.isUploadMissing = requiresUpload && !didUploadImages;
  result.isProofMissing = result.isCommentMissing || result.isUploadMissing;

  return result;
};

/**
 * Enhance the questions with answers and proof data.
 * @param {Array} questions - The questions
 * @param {Array} answers - The answers
 * @param {Array} uploadedImagesKeys - The uploaded image keys
 * @returns {Array}
 */
export const enhanceQuestionsWithAnswersAndProof = (
  questions,
  answers,
  uploadedImagesKeys
) => {
  return questions.map((question) => {
    const savedAnswer = answers.find(
      (answer) => answer.questionId === question.questionId
    );

    let questionImageKeys;
    if (uploadedImagesKeys) {
      questionImageKeys = getQuestionImageKeys(
        uploadedImagesKeys,
        question.questionId
      );
    } else {
      questionImageKeys = savedAnswer?.images?.map((imageKey) =>
        imageKey.replace('public/', '')
      );
    }

    const proofRequirement = shouldAskForProof(
      savedAnswer,
      question,
      questionImageKeys?.length ?? 0
    );

    return {
      ...question,
      savedAnswer,
      questionImageKeys,
      proofRequirement,
    };
  });
};

export const shouldAssessmentBeCheckedForProof = (surveyData) => {
  const { year } = surveyData;
  const { requiredProof } = featureFlags;

  const isYearCovered = year >= requiredProof.startYear;
  return isYearCovered;
};

export const adjustAssessmentsBasedOnFeatureFlags = (
  enhancedQuestionData,
  surveyData
) => {
  const shouldBeCheckedForProof = shouldAssessmentBeCheckedForProof(surveyData);

  if (!shouldBeCheckedForProof) {
    return enhancedQuestionData.map((question) => {
      question.proofRequirement = {
        isCommentMissing: false,
        isUploadMissing: false,
        isProofMissing: false,
      };
      return question;
    });
  }

  return enhancedQuestionData;
};

export const removeTypenameFromAllAnswers = (answerArr) => {
  return answerArr.map((answer) => {
    const answerCopy = { ...answer };
    delete answerCopy['__typename'];
    return answerCopy;
  });
};

export const getActiveStep = (answerArr) => {
  const answersCompleted = answerArr.filter((answer) => answer.isComplete);
  return answersCompleted.length + 1;
};

export const getMultiSelectAnswers = (answerValue, currentAnswerState) => {
  if (currentAnswerState.includes(answerValue)) {
    return currentAnswerState.filter((answerId) => answerId !== answerValue);
  }
  if (answerValue) return [...currentAnswerState, answerValue];
  return currentAnswerState;
};

export const getQuestionImageKeys = (imageKeysArr, questionId) => {
  if (!imageKeysArr) return [];

  const regex = new RegExp(`${questionId}`);
  return imageKeysArr.filter((imageKey) => imageKey.match(regex));
};

export const areAllAnswersComplete = (answers) => {
  if (!answers) return null;
  return answers.every((answer) => answer.isComplete);
};

export const isAnyQuestionMissingProof = (questions) =>
  questions.some((question) => question.proofRequirement.isProofMissing);

// This function is used to normalize the selected answers for single choice questions that have sub-answers.
// It ensures that the main answer is always first in the array, followed by any sub-answers.
export const normalizeSelectedAnswer = (selectedAnswer, answerOptions) => {
  const unique = [...new Set(selectedAnswer)];

  if (!answerOptions?.length) return unique;

  const mainAnswerIds = answerOptions.map((opt) => opt.answerId);
  const mainId = unique.find((id) => mainAnswerIds.includes(id));

  if (!mainId || unique[0] === mainId) return unique;

  // Ensure main answer is first, sub-answers follow
  return [mainId, ...unique.filter((id) => id !== mainId)];
};

// Binds an answer to its question and normalizes the selectedAnswer field.
export const normalizeAnswer = (answer, questions) => {
  const question = questions.find((q) => q.questionId === answer.questionId);
  return {
    ...answer,
    selectedAnswer: normalizeSelectedAnswer(
      answer.selectedAnswer,
      question?.answerOptions
    ),
  };
};

// This function computes the score for a given answer based on the selected parent and sub-answers.
export const computeScore = (
  answerOptions,
  selectedParentId,
  selectedSubIds
) => {
  const selectedOption = answerOptions.find(
    (o) => o.answerId === selectedParentId
  );
  if (selectedOption?.score == null) return 0;

  if (!selectedOption.subAnswerOptions?.length) return selectedOption.score;

  const subOptionIds = new Set(
    selectedOption.subAnswerOptions.map((s) => s.answerId)
  );
  const selectedSubCount = selectedSubIds.filter((id) =>
    subOptionIds.has(id)
  ).length;

  if (selectedSubCount >= (selectedOption.minimumSubAnswers ?? 0)) {
    return selectedOption.score;
  }

  return answerOptions
    .filter((o) => o.score != null)
    .filter((o) => selectedSubCount >= (o.minimumSubAnswers ?? 0))
    .reduce((best, o) => Math.max(best, o.score), 0);
};
